// src/pages/CompanyProfileForm.jsx — بيانات المنشأة (بالتصميم الموحّد)
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, MenuItem, Switch, Button, Tooltip,
  CircularProgress, Alert, InputAdornment,
} from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { toast } from 'react-toastify';
import api from '../../axios';
import { ReportPage, fieldSx, BRAND } from '../../components/Reports/ReportKit';

const BORDER = '#e5e7eb';
const MAX_LOGO = 1024 * 1024; // 1MB

const EMPTY = {
  nameAr: '', nameEn: '', vatNo: '', crn: '', crType: 'CRN', activityAr: '', activityEn: '',
  address: '', buildingNo: '', streetName: '', district: '', city: '', additionalNo: '', postalCode: '',
  phone: '', mobile: '', email: '', website: '', enableZatcaInvoice: false, logoImage: null,
};

// أنواع هوية البائع المعتمدة في الفاتورة الإلكترونية
const CR_TYPES = [
  ['CRN', 'السجل التجاري'],
  ['MOM', 'ترخيص وزارة الشؤون البلدية'],
  ['MLS', 'ترخيص وزارة الموارد البشرية'],
  ['700', 'الرقم الموحد (700)'],
  ['SAG', 'ترخيص وزارة الاستثمار'],
  ['OTH', 'أخرى'],
];

// ── التحقق (تنبيه فقط — لا يمنع الحفظ إلا عند تفعيل الفوترة الإلكترونية) ──
const digits = (v) => String(v || '').replace(/\D/g, '');
const RULES = {
  nameAr: (v) => (!String(v || '').trim() ? 'اسم المنشأة مطلوب' : ''),
  vatNo: (v) => (v && !/^3\d{13}3$/.test(v) ? 'الرقم الضريبي 15 رقمًا ويبدأ وينتهي بالرقم 3' : ''),
  buildingNo: (v) => (v && !/^\d{4}$/.test(v) ? 'رقم المبنى 4 أرقام' : ''),
  additionalNo: (v) => (v && !/^\d{4}$/.test(v) ? 'الرقم الفرعي 4 أرقام' : ''),
  postalCode: (v) => (v && !/^\d{5}$/.test(v) ? 'الرمز البريدي 5 أرقام' : ''),
  email: (v) => (v && !/^\S+@\S+\.\S+$/.test(v) ? 'صيغة البريد غير صحيحة' : ''),
};
// الحقول المطلوبة للفوترة الإلكترونية (ZATCA)
const ZATCA_REQUIRED = ['nameAr', 'vatNo', 'crn', 'buildingNo', 'streetName', 'district', 'city', 'postalCode'];
const LABEL = {
  nameAr: 'اسم المنشأة', vatNo: 'الرقم الضريبي', crn: 'رقم الهوية/السجل', buildingNo: 'رقم المبنى',
  streetName: 'الشارع', district: 'الحي', city: 'المدينة', postalCode: 'الرمز البريدي',
};

function Section({ title, icon, children, action }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafbfc' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: `${BRAND.primary}1a`, color: BRAND.primary, '& svg': { fontSize: 18 } }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
    </Paper>
  );
}

const Cols = ({ n = 2, children }) => (
  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: `repeat(${n}, minmax(0,1fr))` } }}>{children}</Box>
);

export default function CompanyProfileForm() {
  const { companyId: companyIdParam } = useParams();
  const navigate = useNavigate();
  const companyId = parseInt(companyIdParam, 10) || parseInt(localStorage.getItem('companyId'), 10) || 0;

  const [profile, setProfile] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoChanged, setLogoChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/CompanyProfile/${companyId}`)
      .then((res) => {
        const data = { ...EMPTY, ...res.data };
        Object.keys(EMPTY).forEach(k => { if (data[k] == null && k !== 'logoImage') data[k] = EMPTY[k]; });
        const p = { ...data, logoImage: null };
        setProfile(p); setSaved(p);
        setLogoPreview(res.data?.logoImage ? `data:image/png;base64,${res.data.logoImage}` : '');
      })
      .catch((err) => {
        if (err.response?.status === 404) setIsNew(true);
        else setError('فشل جلب بيانات المنشأة.');
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  const set = (name, value) => setProfile(prev => ({ ...prev, [name]: value }));
  const onField = (e) => set(e.target.name, e.target.value);
  const onDigits = (max) => (e) => set(e.target.name, digits(e.target.value).slice(0, max));
  const blur = (e) => setTouched(t => ({ ...t, [e.target.name]: true }));

  const errors = useMemo(() => Object.fromEntries(Object.entries(RULES).map(([k, f]) => [k, f(profile[k])])), [profile]);
  const zatcaMissing = useMemo(() => ZATCA_REQUIRED.filter(k => !String(profile[k] || '').trim()), [profile]);
  const dirty = logoChanged || JSON.stringify({ ...profile, logoImage: null }) !== JSON.stringify(saved);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('اختر ملف صورة'); return; }
    if (file.size > MAX_LOGO) { toast.error('حجم الشعار أكبر من 1 ميجابايت'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoPreview(dataUrl);
      set('logoImage', String(dataUrl).split(',')[1]);
      setLogoChanged(true);
    };
    reader.readAsDataURL(file);
  };
  const removeLogo = () => { setLogoPreview(''); set('logoImage', ''); setLogoChanged(true); };

  const save = useCallback(async () => {
    setError('');
    setTouched(Object.fromEntries(Object.keys(RULES).map(k => [k, true])));
    if (errors.nameAr) { toast.error(errors.nameAr); return; }
    if (profile.enableZatcaInvoice) {
      if (zatcaMissing.length) { toast.error(`الفوترة الإلكترونية تتطلب: ${zatcaMissing.map(k => LABEL[k]).join('، ')}`); return; }
      const bad = Object.entries(errors).find(([, v]) => v);
      if (bad) { toast.error(bad[1]); return; }
    }
    setSubmitting(true);
    try {
      const payload = { companyId, ...profile };
      if (!logoChanged) payload.logoImage = null; // null = بدون تغيير (كما في النسخة السابقة)، "" = إزالة الشعار
      if (isNew) await api.post('/CompanyProfile', payload);
      else await api.put(`/CompanyProfile/${companyId}`, payload);
      setIsNew(false);
      setSaved({ ...profile, logoImage: null });
      setLogoChanged(false);
      toast.success('تم حفظ بيانات المنشأة');
    } catch (err) {
      setError(err?.response?.data?.message || 'حدث خطأ أثناء الحفظ.');
    } finally {
      setSubmitting(false);
    }
  }, [companyId, profile, errors, zatcaMissing, logoChanged, isNew]);

  // Ctrl+S
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (!submitting) save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, submitting]);

  const tf = (name, label, extra = {}) => (
    <TextField
      fullWidth size="small" name={name} label={label} value={profile[name] ?? ''}
      onChange={onField} onBlur={blur} sx={fieldSx}
      error={!!(touched[name] && errors[name])}
      helperText={touched[name] && errors[name] ? errors[name] : extra.helperText}
      {...extra}
    />
  );
  const ltr = { inputProps: { dir: 'ltr', style: { textAlign: 'right' } } };
  const icon = (I) => ({ InputProps: { startAdornment: <InputAdornment position="start"><I sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> } });

  const fullAddress = [
    [profile.buildingNo, profile.streetName].filter(Boolean).join(' '),
    profile.district,
    [profile.city, profile.postalCode].filter(Boolean).join(' '),
  ].filter(Boolean).join('، ') || profile.address;

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>;
  }

  return (
    <ReportPage
      title="بيانات المنشأة"
      subtitle="تظهر هذه البيانات في الفواتير والتقارير والفاتورة الإلكترونية"
      icon={<ApartmentOutlinedIcon />}
      badges={[
        isNew ? { label: 'منشأة جديدة', icon: <ApartmentOutlinedIcon /> }
          : dirty ? { label: 'تغييرات غير محفوظة', icon: <FiberManualRecordIcon sx={{ color: '#FBBF24' }} /> }
          : { label: 'كل التغييرات محفوظة', icon: <CheckCircleOutlineIcon /> },
        ...(profile.enableZatcaInvoice ? [{ label: 'الفوترة الإلكترونية مفعّلة', icon: <VerifiedOutlinedIcon /> }] : []),
      ]}
      actions={[
        { label: 'رجوع', icon: <ArrowForwardIcon />, onClick: () => navigate(-1) },
        { label: submitting ? 'جاري الحفظ...' : 'حفظ', icon: submitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />, onClick: save, primary: true, disabled: submitting },
      ]}
    >
      {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '12px' }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 340px' }, alignItems: 'start' }}>
        {/* ───── الحقول ───── */}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Section title="البيانات التعريفية" icon={<BadgeOutlinedIcon />}>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Cols>
                {tf('nameAr', 'اسم المنشأة (عربي) *')}
                {tf('nameEn', 'Company name (English)', ltr)}
              </Cols>
              <Cols n={3}>
                {tf('vatNo', 'الرقم الضريبي', { ...ltr, onChange: onDigits(15), helperText: `${digits(profile.vatNo).length}/15` })}
                <TextField select fullWidth size="small" name="crType" label="نوع الهوية" value={profile.crType || 'CRN'} onChange={onField} sx={fieldSx}>
                  {CR_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
                {tf('crn', CR_TYPES.find(x => x[0] === (profile.crType || 'CRN'))?.[1] || 'رقم الهوية', ltr)}
              </Cols>
              <Cols>
                {tf('activityAr', 'النشاط التجاري (عربي)')}
                {tf('activityEn', 'Business activity (English)', ltr)}
              </Cols>
            </Box>
          </Section>

          <Section title="معلومات الاتصال" icon={<ContactPhoneOutlinedIcon />}>
            <Cols>
              {tf('phone', 'الهاتف', { ...ltr, ...icon(PhoneOutlinedIcon) })}
              {tf('mobile', 'الجوال', { ...ltr, ...icon(PhoneIphoneOutlinedIcon) })}
              {tf('email', 'البريد الإلكتروني', { ...ltr, ...icon(AlternateEmailOutlinedIcon) })}
              {tf('website', 'الموقع الإلكتروني', { ...ltr, ...icon(LanguageOutlinedIcon) })}
            </Cols>
          </Section>

          <Section title="العنوان الوطني" icon={<LocationOnOutlinedIcon />}>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Cols n={3}>
                {tf('city', 'المدينة')}
                {tf('district', 'الحي')}
                {tf('streetName', 'الشارع')}
              </Cols>
              <Cols n={3}>
                {tf('buildingNo', 'رقم المبنى', { ...ltr, onChange: onDigits(4) })}
                {tf('additionalNo', 'الرقم الفرعي', { ...ltr, onChange: onDigits(4) })}
                {tf('postalCode', 'الرمز البريدي', { ...ltr, onChange: onDigits(5) })}
              </Cols>
              {tf('address', 'عنوان إضافي / وصف الموقع (اختياري)')}
            </Box>
          </Section>
        </Box>

        {/* ───── الشريط الجانبي ───── */}
        <Box sx={{ display: 'grid', gap: 1.5, position: { lg: 'sticky' }, top: { lg: 16 } }}>
          {/* الشعار */}
          <Section title="شعار المنشأة" icon={<PhotoCameraOutlinedIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                onClick={() => fileRef.current?.click()}
                sx={{
                  width: 104, height: 104, borderRadius: '16px', flexShrink: 0, cursor: 'pointer', overflow: 'hidden',
                  display: 'grid', placeItems: 'center', bgcolor: '#f8fafc',
                  border: `2px dashed ${logoPreview ? 'transparent' : '#cbd5e1'}`,
                  boxShadow: logoPreview ? `0 0 0 1px ${BORDER}` : 'none',
                  '&:hover': { borderColor: BRAND.primary },
                }}
              >
                {logoPreview
                  ? <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <Box sx={{ textAlign: 'center', color: '#94a3b8' }}><PhotoCameraOutlinedIcon /><Typography sx={{ fontSize: 11.5 }}>رفع شعار</Typography></Box>}
              </Box>
              <Box sx={{ display: 'grid', gap: 1, flex: 1 }}>
                <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()} startIcon={<PhotoCameraOutlinedIcon />}
                  sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
                  {logoPreview ? 'تغيير الشعار' : 'اختيار صورة'}
                </Button>
                {logoPreview && (
                  <Button size="small" color="error" onClick={removeLogo} startIcon={<DeleteOutlineIcon />}
                    sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
                    إزالة
                  </Button>
                )}
                <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>PNG أو JPG، حتى 1 ميجابايت</Typography>
              </Box>
              <input ref={fileRef} hidden type="file" accept="image/*" onChange={handleLogoChange} />
            </Box>
          </Section>

          {/* الفوترة الإلكترونية */}
          <Paper elevation={0} sx={{
            border: `1px solid ${profile.enableZatcaInvoice ? '#a7f3d0' : BORDER}`, borderRadius: '14px', p: 1.75,
            bgcolor: profile.enableZatcaInvoice ? '#ecfdf5' : '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center',
                bgcolor: profile.enableZatcaInvoice ? '#10b981' : '#f1f5f9', color: profile.enableZatcaInvoice ? '#fff' : '#64748b' }}>
                <VerifiedOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14 }}>الفوترة الإلكترونية (ZATCA)</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>ربط الفواتير بهيئة الزكاة والضريبة والجمارك</Typography>
              </Box>
              <Switch checked={!!profile.enableZatcaInvoice} onChange={e => set('enableZatcaInvoice', e.target.checked)} color="success" />
            </Box>
            {profile.enableZatcaInvoice && zatcaMissing.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.25, borderRadius: '10px', py: 0, fontSize: 12.5 }}>
                بيانات ناقصة: {zatcaMissing.map(k => LABEL[k]).join('، ')}
              </Alert>
            )}
          </Paper>

          {/* معاينة كما تظهر في الفاتورة */}
          <Section title="كما تظهر في الفاتورة" icon={<ReceiptLongOutlinedIcon />}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              {logoPreview
                ? <img src={logoPreview} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }} />
                : <Box sx={{ width: 44, height: 44, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: BRAND.primarySoft, color: BRAND.primary, fontWeight: 900 }}>
                    {(profile.nameAr || '؟').trim().charAt(0)}
                  </Box>}
              <Box sx={{ minWidth: 0, fontSize: 12.5, lineHeight: 1.7, color: '#334155' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{profile.nameAr || 'اسم المنشأة'}</Typography>
                {profile.nameEn && <Box dir="ltr" sx={{ textAlign: 'right', color: '#64748b' }}>{profile.nameEn}</Box>}
                {profile.vatNo && <Box>الرقم الضريبي: <b dir="ltr">{profile.vatNo}</b></Box>}
                {profile.crn && <Box>{CR_TYPES.find(x => x[0] === (profile.crType || 'CRN'))?.[1]}: <b dir="ltr">{profile.crn}</b></Box>}
                {fullAddress && <Box>{fullAddress}</Box>}
                {(profile.phone || profile.mobile) && <Box dir="ltr" sx={{ textAlign: 'right' }}>{[profile.phone, profile.mobile].filter(Boolean).join('  •  ')}</Box>}
              </Box>
            </Box>
          </Section>

          <Tooltip title="Ctrl + S">
            <Button fullWidth variant="contained" onClick={save} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
              sx={{ borderRadius: '12px', fontWeight: 800, py: 1.2, background: BRAND.buttonGradient, '&:hover': { background: BRAND.buttonGradientHover }, '& .MuiButton-startIcon': { ml: 1, mr: -.25 } }}>
              {submitting ? 'جاري الحفظ...' : dirty || isNew ? 'حفظ بيانات المنشأة' : 'محفوظة'}
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </ReportPage>
  );
}
